import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../store/hooks';
import {
    loadDepartureBoardFlights,
    selectDepartureBoard,
    toggleSort,
    selectDepartureBoardFilter,
    setCurrentPage,
} from '../../store/features/departureBoard/departureBoardSlice';
import { selectConfig } from '../../store/features/config/configSlice';
import { dialog, DialogDimension, DialogSize } from '@microsoft/teams-js';
import { getBaseUrl } from '../../configVariables';
import { useDepartureBoardPageStyles } from './DepartureBoardPage.styles';
import {
    mergeClasses,
    Toolbar,
    ToolbarButton,
    ToolbarGroup,
    TableHeaderCell,
    Table,
    TableHeader,
    TableRow,
    TableBody,
    TableCell,
    useTableFeatures,
    useTableSort,
    TableColumnId,
    useTableColumnSizing_unstable,
    useTableSelection,
    TableSelectionCell,
    Text,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuButton,
} from '@fluentui/react-components';
import {
    Dismiss24Regular,
    MailAddFilled,
    MailArrowClockwiseFilled,
    MegaphoneRegular,
} from '@fluentui/react-icons';
import PaginationBar from '../PaginationBar';
import { useToolbarStyles } from '../../controls';
import NewCustomerMessageDialog from './NewCustomerMessageDialog/NewCustomerMessageDialog';
import DepartureBoardFiltersPopover from './components/DepartureBoardFiltersPopover';
import {
    createDepartureBoardColumnSizingOptions,
    createDepartureBoardColumns,
    unsortableColumns,
} from './components/departureBoardColumns';
import {
    useDepartureBoardDialogs,
    useDepartureBoardFilters,
    useDepartureBoardPageData,
} from './hooks';
import { newUuid } from '../../utils/uuid';

const DepartureBoardPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const styles = useDepartureBoardPageStyles();
    const toolbarStyles = useToolbarStyles();
    const departureBoardState = useSelector(selectDepartureBoard);
    const departureBoardFilterState = useSelector(selectDepartureBoardFilter);

    const { botId } = useSelector(selectConfig);

    const { loadFlightsDebounced } = useDepartureBoardPageData(
        departureBoardState.loadStatus
    );

    const {
        customerMessageDialog,
        openSendMessageToTeamDialog,
        openAddMisconnectedCustomersDialog,
        openCustomerMessageDialog,
    } = useDepartureBoardDialogs(botId);

    const {
        onQueryTextChange,
        onClearSearchTextClick,
        onFilterClick,
        onClearFiltersClick,
        onDpartureStatusFilterChange,
        onDepartureAirportFilterChange,
        onArrivalAirportFilterChange,
        onCheckInStatusChange,
        onDelayedBaggageStatusChange,
        onMisconnectedCustomersStatusChange,
        onConnectionStatusChange,
        onHaulTypeChange,
    } = useDepartureBoardFilters({
        open: departureBoardFilterState.open,
        queryText: departureBoardFilterState.queryText,
        loadFlightsDebounced,
    });

    const columns = useMemo(
        () =>
            createDepartureBoardColumns({
                t,
                styles,
                openSendMessageToTeamDialog,
                openAddMisconnectedCustomersDialog,
                openCustomerMessageDialog,
            }),
        [
            t,
            styles,
            openSendMessageToTeamDialog,
            openAddMisconnectedCustomersDialog,
            openCustomerMessageDialog,
        ]
    );

    const columnSizingOptions = useMemo(
        () => createDepartureBoardColumnSizingOptions(),
        []
    );

    const {
        getRows,
        selection: {
            allRowsSelected,
            someRowsSelected,
            toggleAllRows,
            toggleRow,
            isRowSelected,
            selectedRows,
            clearRows,
        },
        sort: { getSortDirection },
        columnSizing_unstable: columnSizing,
    } = useTableFeatures(
        {
            getRowId: (item) => item.id,
            columns,
            items: departureBoardState.items.map((x) => ({
                ...x,
                selected: false,
            })),
        },
        [
            useTableSort({
                sortState: {
                    sortColumn: departureBoardState.sorting.sortBy,
                    sortDirection: departureBoardState.sorting.sortByDescending
                        ? 'ascending'
                        : 'descending',
                },
            }),
            useTableColumnSizing_unstable({
                columnSizingOptions,
                autoFitColumns: false,
            }),
            useTableSelection({
                selectionMode: 'multiselect',
            }),
        ]
    );

    const rows = getRows((row) => {
        const selected = isRowSelected(row.rowId);
        return {
            ...row,
            onClick: (e: React.MouseEvent) => toggleRow(e, row.rowId),
            selected,
            appearance: selected ? ('brand' as const) : ('none' as const),
        };
    });

    const openBroadcastMessagingDialog = useCallback(
        (append: boolean) => {
            const baseUrl = getBaseUrl();

            const dataKey = newUuid();
            localStorage.setItem(dataKey, Array.from(selectedRows).join(','));

            const size: DialogSize = append
                ? {
                      width: DialogDimension.Large,
                      height: DialogDimension.Large,
                  }
                : {
                      width: 500,
                      height: 300,
                  };
            dialog.url.bot.open(
                {
                    size,
                    title: `Broadcast to ${selectedRows?.size} selected flights`,
                    url: `${baseUrl}/broadcastMessaging/${dataKey}?append=${append}`,
                    completionBotId: botId,
                },
                () => {
                    clearRows({} as React.MouseEvent);
                }
            );
        },
        [botId, clearRows, selectedRows]
    );

    const headerSortProps = useCallback(
        (columnId: TableColumnId) => ({
            onClick: () => {
                if (unsortableColumns.has(columnId)) {
                    return;
                }
                dispatch(toggleSort(columnId as string));
                dispatch(loadDepartureBoardFlights());
            },
            sortDirection: getSortDirection(columnId),
        }),
        [getSortDirection, dispatch]
    );

    const selectedRowsButton = useMemo(() => {
        return !!selectedRows.size ? (
            <div className={styles.selectedRowsButton}>
                <ToolbarButton
                    appearance="subtle"
                    icon={<Dismiss24Regular />}
                    onClick={clearRows}
                ></ToolbarButton>
                <Text weight="semibold">
                    {t('Selected_Rows', { count: selectedRows.size })}
                </Text>
            </div>
        ) : (
            <></>
        );
    }, [clearRows, selectedRows.size, styles.selectedRowsButton, t]);

    return (
        <div className={styles.root}>
            <Toolbar aria-label="Flights" className={toolbarStyles.toolbar}>
                <ToolbarGroup
                    className={toolbarStyles.toolbarGroup}
                    role="presentation"
                >
                    {selectedRowsButton}
                </ToolbarGroup>
                <ToolbarGroup
                    role="presentation"
                    className={mergeClasses(
                        toolbarStyles.toolbarGroup,
                        toolbarStyles.toolbarRightGroup
                    )}
                >
                    <Menu positioning="below-end">
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton
                                disabled={selectedRows.size === 0}
                                icon={<MegaphoneRegular />}
                                appearance="subtle"
                            >
                                {t('Broadcast')}
                            </MenuButton>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem
                                    onClick={() =>
                                        openBroadcastMessagingDialog(false)
                                    }
                                    icon={<MailAddFilled />}
                                >
                                    {t('Broadcast_NewMessage')}
                                </MenuItem>
                                <MenuItem
                                    onClick={() =>
                                        openBroadcastMessagingDialog(true)
                                    }
                                    icon={<MailArrowClockwiseFilled />}
                                >
                                    {t('Broadcast_ExistingMessage')}
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <DepartureBoardFiltersPopover
                        open={departureBoardFilterState.open}
                        filterState={departureBoardFilterState}
                        styles={styles}
                        toolbarStyles={toolbarStyles}
                        onToggleOpen={onFilterClick}
                        onClearFiltersClick={onClearFiltersClick}
                        onDepartureStatusFilterChange={
                            onDpartureStatusFilterChange
                        }
                        onDepartureAirportFilterChange={
                            onDepartureAirportFilterChange
                        }
                        onArrivalAirportFilterChange={
                            onArrivalAirportFilterChange
                        }
                        onCheckInStatusChange={onCheckInStatusChange}
                        onDelayedBaggageStatusChange={
                            onDelayedBaggageStatusChange
                        }
                        onMisconnectedCustomersStatusChange={
                            onMisconnectedCustomersStatusChange
                        }
                        onConnectionStatusChange={onConnectionStatusChange}
                        onHaulTypeChange={onHaulTypeChange}
                        onQueryTextChange={onQueryTextChange}
                        onClearSearchTextClick={onClearSearchTextClick}
                    />
                </ToolbarGroup>
            </Toolbar>
            <Table
                className={styles.table}
                size="small"
                role="grid"
                aria-label="Flights"
                noNativeElements
                sortable
            >
                <TableHeader>
                    <TableRow className={styles.tableRow}>
                        <TableSelectionCell
                            className={styles.tableSelectionCell}
                            checked={
                                allRowsSelected
                                    ? true
                                    : someRowsSelected
                                      ? 'mixed'
                                      : false
                            }
                            onClick={toggleAllRows}
                            checkboxIndicator={{
                                'aria-label': 'Select all rows ',
                            }}
                        />
                        {columns.map((column) => (
                            <TableHeaderCell
                                key={column.columnId}
                                style={
                                    columnSizing.getTableHeaderCellProps(
                                        column.columnId
                                    )?.style
                                }
                                {...headerSortProps(column.columnId)}
                            >
                                {column.renderHeaderCell()}
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(({ item, selected, appearance, onClick }) => (
                        <TableRow
                            key={item.id}
                            className={styles.tableRow}
                            onClick={onClick}
                            aria-selected={selected}
                            appearance={appearance}
                        >
                            <TableSelectionCell
                                className={styles.tableSelectionCell}
                                checked={selected}
                                role="gridcell"
                                checkboxIndicator={{
                                    'aria-label': 'Select row',
                                }}
                            />
                            {columns.map((column) => (
                                <TableCell
                                    key={column.columnId}
                                    tabIndex={0}
                                    role="gridcell"
                                    style={{
                                        ...columnSizing.getTableCellProps(
                                            column.columnId
                                        )?.style,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {column.renderCell({ ...item, selected })}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <PaginationBar
                currentPage={departureBoardState.pagination.currentPage}
                pageSize={departureBoardState.pagination.pageSize}
                totalItems={departureBoardState.pagination.totalCount}
                onChange={(page) => {
                    loadFlightsDebounced.cancel();
                    dispatch(setCurrentPage(page));
                    dispatch(loadDepartureBoardFlights());
                }}
            />
            <NewCustomerMessageDialog
                open={customerMessageDialog.isOpen}
                flightTeamId={customerMessageDialog.value ?? ''}
                onOpenChange={customerMessageDialog.onOpenChangeCallback}
            />
        </div>
    );
};

export default DepartureBoardPage;
